// ConfirmActionDialog — explicit-consequence confirm step for mutations
// (P1-E10-2 / AC-STATE-3). Irreversible or outward-facing actions (approve,
// reject, deny, revoke, promote, archive, route-for-approval) confirm with a
// consequence statement and — where governance requires it — a typed rationale
// that is never boilerplate-autofilled (AC-FIN-2.1).
//
// Import directly by path (not via components/index.js) to keep this additive:
//   import { ConfirmActionDialog } from '../../components/primitives/ConfirmActionDialog.jsx';
import { useEffect, useRef, useState } from 'react';
import { FormField } from '../primitives.jsx';
import './confirmActionDialog.redesign.css';

export function ConfirmActionDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary', // 'primary' | 'danger'
  requireNote = false,
  // Show the note field without making it mandatory (e.g. optional decision
  // notes on advance actions). Defaults to requireNote.
  showNote,
  noteLabel = 'Rationale',
  noteHint,
  notePlaceholder,
  confirmEnabled = true,
  requireExactText = null,
  busy = false,
  onConfirm,
  onCancel,
}) {
  const [note, setNote] = useState('');
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const noteInputRef = useRef(null);
  const confirmButtonRef = useRef(null);

  // Reset the typed note whenever the dialog is (re)opened for a new action.
  useEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement;
    setNote('');
  }, [open]);

  useEffect(() => {
    if (!open) {
      openerRef.current?.focus?.();
      return undefined;
    }
    const target = noteVisible ? noteInputRef.current : cancelButtonRef.current || confirmButtonRef.current;
    target?.focus?.();
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onKeyDown(event) {
      if (event.key === 'Escape' && !busy) onCancel?.();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const trimmedNote = note.trim();
  const noteVisible = showNote ?? requireNote;
  const exactTextBlocked = requireExactText != null && note !== requireExactText;
  const confirmBlocked = busy || (requireNote && !trimmedNote) || exactTextBlocked || !confirmEnabled;

  return (
    <div
      className="confirm-dialog__overlay"
      data-testid="confirm-action-dialog"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel?.();
      }}
    >
      <div
        ref={dialogRef}
        className={`confirm-dialog tone-${tone}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="confirm-dialog"
      >
        <h3 className="confirm-dialog__title">{title}</h3>
        {body ? <p className="confirm-dialog__body">{body}</p> : null}
        {noteVisible ? (
          <FormField
            label={noteLabel}
            htmlFor="confirm-dialog-note"
            required={requireNote}
            hint={
              noteHint ||
              (requireNote
                ? 'A typed rationale is required — it is recorded with the decision.'
                : 'Optional — recorded with the decision when provided.')
            }
          >
            <textarea
              ref={noteInputRef}
              id="confirm-dialog-note"
              className="form-input form-textarea"
              rows={3}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={notePlaceholder || 'Document the basis for this decision.'}
              disabled={busy}
              data-testid="confirm-dialog-note"
            />
          </FormField>
        ) : null}
        <div className="confirm-dialog__actions">
          <button
            ref={cancelButtonRef}
            type="button"
            className="btn ghost sm"
            onClick={onCancel}
            disabled={busy}
            data-testid="confirm-dialog-cancel"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            className={`btn sm ${tone === 'danger' ? 'danger' : 'primary'}`}
            onClick={() => onConfirm?.(trimmedNote)}
            disabled={confirmBlocked}
            data-testid="confirm-dialog-confirm"
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
